import { makeAutoObservable, runInAction } from "mobx";

import type { RootStore } from "@/store";

export type HintKey = keyof IntlMessages["hints"]["items"];

/** Plain values: a tip renders both as rich text in the panel and as an aria-description */
export type HintParams = Record<string, string | number>;

export type Hint = {
  key: HintKey;
  params?: HintParams;
};

type TrackedHint = { target: Element; hint: Hint };

export class HintsStore {
  rootStore: RootStore;

  private _hint: Hint | null = null;

  private timeoutId: NodeJS.Timeout | null = null;

  private focused: TrackedHint | null = null;

  private hovered: TrackedHint | null = null;

  static readonly HINT_SHOW_DELAY_MS = 300;

  constructor(rootStore: RootStore) {
    this.rootStore = rootStore;
    makeAutoObservable(this);
  }

  /**
   * Hover and focus are tracked while the panel is closed too, so opening it shows the current tip.
   * Hover wins over focus: the pointer's tip shows, and focus's tip returns when it leaves.
   */
  showFocused(target: Element, hint: Hint) {
    this.focused = { target, hint };
    this.hint = this.liveHint(this.hovered) ?? hint;
  }

  hideFocused(target: Element) {
    if (this.focused?.target !== target) return;

    this.focused = null;
    this.hint = this.liveHint(this.hovered);
  }

  showHovered(target: Element, hint: Hint) {
    this.hovered = { target, hint };
    this.hint = hint;
  }

  hideHovered(target: Element) {
    if (this.hovered?.target !== target) return;

    this.hovered = null;
    this.hint = this.liveHint(this.focused);
  }

  /** A control can unmount without a mouseleave or blur, e.g. a menu item closed with Escape */
  private liveHint(tracked: TrackedHint | null): Hint | null {
    return tracked?.target.isConnected ? tracked.hint : null;
  }

  get hint(): Hint | null {
    if (this.rootStore.view.isHintsPanelVisible) {
      return this._hint;
    }

    return null;
  }

  set hint(newHint: Hint | null) {
    if (this.timeoutId) {
      clearTimeout(this.timeoutId);
      this.timeoutId = null;
    }

    if (newHint) {
      this._hint = newHint;
    } else {
      this.timeoutId = setTimeout(() => {
        runInAction(() => (this._hint = null));
      }, HintsStore.HINT_SHOW_DELAY_MS);
    }
  }

  dispose() {
    this._hint = null;

    if (this.timeoutId) {
      clearTimeout(this.timeoutId);
      this.timeoutId = null;
    }
  }
}
